import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

import { useQueryClient } from '@tanstack/react-query';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { FormDocumentRow } from '../../../components/Form/FormDocumentRow';
import { fetchPloById, savePloDraft, submitPloForm } from '../../../services/PloOneService';
import type { PLOFormOneData } from '../../../types/PLOs/plo_one.type';
import { FormActionButtons } from '../../../components/Form/FormActionButton';
import { SignatureModal } from '../../../components/Form/SignatureModel';
// import { AttachmentSection } from '../../../components/Form/AttachmentSection';
import { PageLayoutWrapper } from '../../../components/FormPageLayoutWrapper';
import type { GeoLevel } from '../../../types/database';
import type { User } from '../../../types/user';
// import { StageProgression } from '../../../components/Form/StateProgression';

const INITIAL_PLO_FORM_DATA: PLOFormOneData = {
  id: '',
  courtName: '',
  caseNumber: '',
  advocateOfficeName: '',
  plaintiffOrAccused: '',
  policeStationInfoAndNumber: '',
  policeReportDate: '',
  caseSummary: '',
  chargeSection: '',
  chargeSectionDate: '',
  witnessDetails: '',
  interviewerDetails: '',
  interviewDate: '',
  interviewLocation: '',
  supportingOrganization: '',
  observerName: '',
  courtSummonsDate: '',
  finalOrderDetails: '',
  finalOrderDate: '',
  submitDate: '',
  signatureImage: '',
  signName: '',
  signerName: '',
  attachments: [],
  status: 'DRAFT',
};

interface LegalFormProps {
  id?: string;
  initialData?: Partial<PLOFormOneData>;
}

export function PLOFormOne({ id: propId, initialData }: LegalFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const id = propId || params.id;
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const [formData, setFormData] = useState<PLOFormOneData>({
    ...INITIAL_PLO_FORM_DATA,
    ...initialData,
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean>(
    initialData?.status === 'APPROVED' || formData.status === 'APPROVED'
  );
  const [selectedGeoLevels, setSelectedGeoLevels] = useState<GeoLevel[]>([]);

  const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null
  
  useEffect(() => {
    if (formData.status === 'APPROVED') {
      setIsApproved(true);
    }
  }, [formData.status]);

  // Fetch form data by ID
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchPloById(id)
      .then((data: PLOFormOneData) => {
        if (isSubscribed) {
          setFormData((prev) => ({
            ...prev,
            ...data,
            id: data.id || prev.id || id || '',
          }));
          if (data.status === 'APPROVED') {
            setIsApproved(true);
          }
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Form is editable ONLY if status is DRAFT/RETURNED AND it has NOT been approved yet
  const isEditable = (formData.status === 'DRAFT') && !isApproved;

  const isFormSigned = Boolean(
    formData.signatureImage?.trim() && 
    (formData.signerName?.trim() || formData.signName?.trim())
  );

  // Navigation Handler
  const handleBackNavigation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ploFormOne'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/plo-form-one');
    }
  };

  // Form Field Event Handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearDate = (name: keyof PLOFormOneData) => {
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, [name]: '' }));
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!isEditable || !e.target.files) return;
  //   const filesArray = Array.from(e.target.files);
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: [...(prev.attachments || []), ...filesArray],
  //   }));
  // };

  // const handleRemoveFile = (index: number) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: (prev.attachments || []).filter((_, i) => i !== index),
  //   }));
  // };

  const handleSaveSignature = () => {
    if (!isEditable) return;
    if (sigCanvasRef.current) {
      if (sigCanvasRef.current.isEmpty()) {
        alert('လက်မှတ် အရင်ရေးထိုးပေးပါရန်။');
        return;
      }
      try {
        const canvas = sigCanvasRef.current.getTrimmedCanvas();
        const base64Data = canvas.toDataURL('image/png');
        setFormData((prev) => ({ ...prev, signatureImage: base64Data }));
        setIsSigning(false);
      } catch {
        const rawCanvas = sigCanvasRef.current.getCanvas();
        const base64Data = rawCanvas.toDataURL('image/png');
        setFormData((prev) => ({ ...prev, signatureImage: base64Data }));
        setIsSigning(false);
      }
    }
  };

  const handleClearSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, signatureImage: '' }));
  };

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditable || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Partial<PLOFormOneData> = { ...formData, status: 'DRAFT' };

      const response = await savePloDraft(formData.id || id || null, payload);

      if (response && response.id) {
        setFormData((prev) => ({ ...prev, id: response.id, status: 'DRAFT' }));
      }

      alert('မူကြမ်း သိမ်းဆည်းပြီးပါပြီ (Draft Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      alert(err?.message || 'မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Approve & Save (အတည်ပြုသိမ်းဆည်းမည်)
  const handleApproveAndSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!formData.submitDate?.trim()) {
      alert('တင်ပြသည့်ရက်စွဲ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    const nameOfSigner = (formData.signerName || formData.signName || '').trim();
    if (!formData.signatureImage?.trim() || !nameOfSigner) {
      alert('လက်မှတ် နှင့် အမည် ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      const payload: PLOFormOneData = {
        ...formData,
        id: targetId || '',
        signerName: nameOfSigner,
        signName: nameOfSigner,
        status: 'APPROVED',
      };

      const response = await savePloDraft(targetId || null, payload);
      const newId = response?.id || targetId;

      setFormData((prev) => ({
        ...prev,
        id: newId,
        signerName: nameOfSigner,
        signName: nameOfSigner,
        status: 'APPROVED',
      }));

      setIsApproved(true); // Locks form editing
      alert('အတည်ပြု သိမ်းဆည်းပြီးပါပြီ (Approved & Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to approve and save:', err);
      alert(err?.message || 'အတည်ပြု သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Submit To Superior (အထက်သို့ တင်ပြမည်)
  const handleSubmitToSuperior = async (
  e: React.MouseEvent,
  targetLevels?: GeoLevel[] | null
) => {
  e.preventDefault();
  if (isSubmitting) return;

  if (!formData.submitDate?.trim()) {
    alert('တင်ပြသည့်ရက်စွဲ ဖြည့်သွင်းပေးပါရန်။');
    return;
  }

  // Use passed levels if available; otherwise use multi-select state
  const levelsToSubmit =
    targetLevels && targetLevels.length > 0 ? targetLevels : selectedGeoLevels;

  // Validate that at least one checkbox level is selected
  if (!levelsToSubmit || levelsToSubmit.length === 0) {
    alert('အထက်သို့ တင်ပြရန် အဆင့် (Level) အနည်းဆုံးတစ်ခု ရွေးချယ်ပေးပါရန်။');
    return;
  }

  try {
    setIsSubmitting(true);
    let targetId: string | undefined = formData.id || id;

    if (!targetId) {
      const draftResponse = await savePloDraft(null, { ...formData, status: 'DRAFT' });
      targetId = draftResponse?.id;
      if (!targetId) {
        throw new Error('အချက်အလက် သိမ်းဆည်းခြင်း အိုင်ဒီ (ID) မရရှိပါ');
      }

      setFormData((prev) => ({
        ...prev,
        ...draftResponse,
        id: targetId!,
      }));
    }

    const nameOfSigner = (formData.signerName || formData.signName || '').trim();
    const hasSigned = Boolean(formData.signatureImage?.trim() && nameOfSigner);

    if (!hasSigned) {
      throw new Error('လက်မှတ်ထိုးပြီးမှသာ တင်ပြနိုင်ပါသည်');
    }

    // Attach plural array property to payload
    const payload: PLOFormOneData = {
      ...formData,
      id: targetId,
      signerName: nameOfSigner,
      signName: nameOfSigner,
      status: 'SUBMITTED',
      targetGeoLevels: levelsToSubmit, // Array of selected levels
    };

    const response = await submitPloForm(targetId, payload);

    setFormData((prev) => ({
      ...prev,
      id: targetId,
      status: response?.status || 'SUBMITTED',
    }));

    setIsApproved(true);
    alert(
      response?.status === 'SUBMITTED'
        ? 'အထက်သို့ တင်ပြပြီးပါပြီ (Submitted Successfully)'
        : 'တင်ပြမှုမအောင်မြင်ပါ'
    );
    navigate('/plo-form-one');
  } catch (err: any) {
    console.error('Failed to submit form:', err);
    alert(err?.message || 'တင်ပြခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
  } finally {
    setIsSubmitting(false);
  }
};

  const renderDocumentRow = (label: string, name: keyof PLOFormOneData) => (
    <FormDocumentRow
      label={label}
      name={name as string}
      value={formData[name] as string}
      isEditable={isEditable}
      onChange={handleChange}
    />
  );

  const renderDatePicker = (name: keyof PLOFormOneData) => (
    <FormDatePicker
      name={name as string}
      value={(formData?.[name] as string) || ''}
      isEditable={isEditable}
      onChange={handleChange}
      onClear={handleClearDate}
    />
  );

  // const renderStageProgression = () => {
  //   const status = formData.status || 'DRAFT';

  //   const STATUS_LABELS: Record<string, string> = {
  //     APPROVED: 'ကိုယ်တိုင်အတည်ပြုပြီး',
  //     SUBMITTED: 'တင်ပြပြီး',
  //     DRAFT: 'မူကြမ်း',
  //     UNDER_REVIEW: 'ကြည့်ရှုထားသည်',
  //   };

  //   return (
  //     <div className="flex items-center gap-2 text-xs font-semibold bg-slate-50 p-2.5 rounded-lg border border-slate-200">
  //       <span className="text-slate-500">အဆင့်:</span>
  //       <span
  //         className={`px-2 py-0.5 rounded text-white ${
  //           status === 'APPROVED'
  //             ? 'bg-emerald-600'
  //             : status === 'SUBMITTED'
  //             ? 'bg-amber-600'
  //             : 'bg-amber-600'
  //         }`}
  //       >
  //         {STATUS_LABELS[status] || STATUS_LABELS.DRAFT}
  //       </span>
  //     </div>
  //   );
  // };

  // const renderStageProgression = () => (
  //     <StageProgression
  //       status={formData.status}
  //       error={error}
  //       onErrorDismiss={() => setError(null)}
  //       targetGeoLevels={formData.targetGeoLevels}
  //       stageHistory={formData.stageHistory}
  //     />
  //   );
  // if (loading) {
  //   return (
  //     <div className="flex items-center justify-center p-12 text-slate-500">
  //       <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
  //     </div>
  //   );
  // }

  const GEO_LEVEL_MAP: Record<string, string> = {
    NATIONAL: 'ရုံးချုပ်',
    STATE: 'ပြည်နယ်/တိုင်း',
    DISTRICT: 'ခရိုင်',
    TOWNSHIP: 'မြို့နယ်',
  };

  const renderStageProgression = () => {
    const status = formData.status || 'DRAFT';

    // Extract and format Geo Level text
    const rawGeo = formData.targetGeoLevels || formData.geoLevel;
    const formatGeo = (geo: any) => {
      if (typeof geo === 'string') return GEO_LEVEL_MAP[geo] || geo;
      if (Array.isArray(geo)) {
        return geo
          .map((g) => GEO_LEVEL_MAP[g] || g)
          .filter(Boolean)
          .join(' | ');
      }
      return '';
    };
    const geoText = formatGeo(rawGeo);

    const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
    > = {
      DRAFT: {
        label: 'မူကြမ်း',
        bg: 'bg-slate-100',
        text: 'text-slate-600',
        border: 'border-slate-200',
        dot: 'bg-slate-400',
      },
      APPROVED: {
        label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        dot: 'bg-emerald-600',
      },
      SUBMITTED: {
        label: 'တင်ပြပြီး',
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200',
        dot: 'bg-amber-500',
      },
      UNDER_REVIEW: {
        label: 'ကြည့်ရှုထားသည်',
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        dot: 'bg-blue-500',
      },
      ATTACHED: {
        label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        dot: 'bg-blue-500',
      },
    };

    const currentConfig = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

      if (loading) {
        return (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
          </div>
        );
      }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 px-3.5 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
        {/* Left: Section Title & Location Subtext */}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            လုပ်ငန်းစဉ် အခြေအနေ
          </span>
          {geoText && (
            <span className="text-xs text-slate-600 font-medium mt-0.5 flex items-center gap-1">
              <span className="text-slate-400">📍</span>
              <span>{geoText}</span>
            </span>
          )}
        </div>

        {/* Right: Modern Pill Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentConfig.bg} ${currentConfig.text} ${currentConfig.border} self-start sm:self-center`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
          <span>{currentConfig.label}</span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 my-6 max-w-xl mx-auto">
        <p className="font-semibold">{error}</p>
        <button
          type="button"
          onClick={handleBackNavigation}
          className="mt-4 px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          နောက်သို့ ပြန်သွားမည်
        </button>
      </div>
    );
  }

 return (
  <PageLayoutWrapper
      handleBackNavigation={handleBackNavigation}
      renderStageProgression={renderStageProgression}
      isEditable={isEditable}
      isPreview={isPreview}
      readOnlyMessage={
        formData.status === 'APPROVED'
          ? 'စာရွက်စာတမ်းကို ကိုယ်တိုင်အတည်ပြုပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
          : formData.status === 'SUBMITTED'
            ? 'စာရွက်စာတမ်းကို အထက်သို့ တင်ပြပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
            : undefined
      }
      setIsPreview={setIsPreview}
      >

    {/* A4 Sheet Container */}
    <div className="bg-white p-6 md:p-12 rounded-xl border border-slate-200 shadow-sm select-text print:border-none print:shadow-none print:p-0">
      <div className="flex justify-end items-center mb-6">
        {/* <span className="text-xs text-slate-400">ID: {formData.id || 'N/A'}</span> */}
        <span className="text-xs font-semibold text-slate-600 tracking-wider">PTI ပုံစံ</span>
      </div>

      <div className="text-center mb-10">
        <h2 className="text-xl font-bold text-slate-900">
          အကြိုတွေ့ဆုံမေးမြန်းခြင်း ပြီးပြတ်ချက်ပုံစံ
        </h2>
      </div>

      <form className="space-y-4">
        {renderDocumentRow('၁။ တရားရုံးအမည်', 'courtName')}
        {renderDocumentRow('၂။ အမှုအမှတ်', 'caseNumber')}
        {renderDocumentRow('၃။ ရှေ့ပြေးဥပဒေရုံးအမည်', 'advocateOfficeName')}
        {renderDocumentRow('၄။ တရားလို / တရားခံအမည်', 'plaintiffOrAccused')}

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-2">
          <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
            ၅။ ရဲစခန်းအမည်၊ ပထမသတင်းပေးတိုင်ချက်အမှတ်
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-grow w-full">
            <div className="flex items-center">
              {isPreview || !isEditable ? (
                <span className="text-sm font-medium text-slate-900 block w-full border-0 border-b border-dashed border-slate-400 min-h-[1.5rem] px-1">
                  {formData.policeStationInfoAndNumber || <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>}
                </span>
              ) : (
                <input
                  type="text"
                  name="policeStationInfoAndNumber"
                  value={formData.policeStationInfoAndNumber}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b border-dashed border-slate-400 p-0 pb-0.5 text-sm text-slate-900 focus:outline-none focus:ring-0 font-medium"
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium shrink-0">ရက်စွဲ -</span>
              <div className="flex-grow">{renderDatePicker('policeReportDate')}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1.5 py-2">
          <label className="text-slate-800 font-medium text-sm">၆။ အမှုအကြောင်းအရာအကျဉ်းချုပ်</label>
          <div className="relative w-full">
            {isPreview || !isEditable ? (
              <p className="text-sm text-slate-900 font-medium leading-[28px] whitespace-pre-wrap bg-transparent p-1 rounded min-h-[6rem] border-0 border-b border-dashed border-slate-400">
                {formData.caseSummary || <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>}
              </p>
            ) : (
              <textarea
                name="caseSummary"
                rows={4}
                value={formData.caseSummary}
                onChange={handleChange}
                className="w-full bg-transparent border-0 p-1 text-sm text-slate-900 focus:outline-none focus:ring-0 font-medium resize-none"
                style={{
                  backgroundImage: 'linear-gradient(transparent, transparent 27px, #94a3b8 27px, #94a3b8 28px)',
                  backgroundSize: '100% 28px',
                  lineHeight: '28px',
                }}
              />
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-2">
          <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
            ၇။ တရားစွဲတင်ပို့သည့် ဥပဒေပုဒ်မ
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-grow w-full">
            <div className="flex items-center">
              {isPreview || !isEditable ? (
                <span className="text-sm font-medium text-slate-900 block w-full border-0 border-b border-dashed border-slate-400 min-h-[1.5rem] px-1">
                  {formData.chargeSection || <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>}
                </span>
              ) : (
                <input
                  type="text"
                  name="chargeSection"
                  value={formData.chargeSection}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b border-dashed border-slate-400 p-0 pb-0.5 text-sm text-slate-900 focus:outline-none focus:ring-0 font-medium"
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium shrink-0">ရက်စွဲ -</span>
              <div className="flex-grow">{renderDatePicker('chargeSectionDate')}</div>
            </div>
          </div>
        </div>

        {renderDocumentRow('၈။ သက်သေအမည် / အသက်', 'witnessDetails')}
        {renderDocumentRow('၉။ တွေ့ဆုံမေးမြန်းသူ (အမှုလိုက်ဥပဒေအရာရှိ)', 'interviewerDetails')}

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
          <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
            ၁၀။ အကြိုတွေ့ဆုံမေးမြန်းမှုပြုလုပ်သည့်ရက်စွဲ
          </label>
          <div className="flex-grow max-w-xs">{renderDatePicker('interviewDate')}</div>
        </div>

        {renderDocumentRow('၁၁။ အကြိုတွေ့ဆုံမေးမြန်းမှုပြုလုပ်သည့်နေရာ', 'interviewLocation')}
        {renderDocumentRow('၁၂။ အကူအညီပေးသည့် အဖွဲ့အစည်း', 'supportingOrganization')}
        {renderDocumentRow('၁၃။ တက်ရောက်လေ့လာသူ', 'observerName')}

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2">
          <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
            ၁၄။ တရားရုံးသက်သေခံသည့်ရက်စွဲ
          </label>
          <div className="flex-grow max-w-xs">{renderDatePicker('courtSummonsDate')}</div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-2">
          <label className="text-slate-800 font-medium text-sm sm:w-80 shrink-0">
            ၁၅။ အပြီးသတ်အမိန့်ချသည့် အမိန့်
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-grow w-full">
            <div className="flex items-center">
              {isPreview || !isEditable ? (
                <span className="text-sm font-medium text-slate-900 block w-full border-0 border-b border-dashed border-slate-400 min-h-[1.5rem] px-1">
                  {formData.finalOrderDetails || <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>}
                </span>
              ) : (
                <input
                  type="text"
                  name="finalOrderDetails"
                  value={formData.finalOrderDetails}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b border-dashed border-slate-400 p-0 pb-0.5 text-sm text-slate-900 focus:outline-none focus:ring-0 font-medium"
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium shrink-0">ရက်စွဲ -</span>
              <div className="flex-grow">{renderDatePicker('finalOrderDate')}</div>
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="pt-12 flex flex-col items-end gap-1.5 text-sm text-slate-800 pr-4">
          <div className="w-72 space-y-3">
            <div className="flex items-center">
              <span className="w-24 text-right text-sm whitespace-nowrap mr-2">လက်မှတ်</span>
              {isPreview || !isEditable ? (
                <div className="flex-1 min-h-[32px] flex items-center justify-center border-0 border-b border-dashed border-slate-400">
                  {formData.signatureImage ? (
                    <img src={formData.signatureImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" />
                  ) : (
                    <span className="text-slate-300 italic text-xs">(လက်မှတ်မရှိပါ)</span>
                  )}
                </div>
              ) : (
                <div className="flex-1 relative flex items-center">
                  <div
                    onClick={() => setIsSigning(true)}
                    className="w-full bg-transparent border-0 border-b border-dashed border-slate-400 min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-slate-50 transition relative group"
                  >
                    {formData.signatureImage ? (
                      <>
                        <img src={formData.signatureImage} alt="Signature" className="h-8 object-contain mix-blend-multiply" />
                        <span className="absolute inset-0 bg-black/40 text-white text-xs opacity-0 group-hover:opacity-100 flex items-center justify-center transition rounded print:hidden">
                          လက်မှတ်အသစ်ထိုးရန် နှိပ်ပါ
                        </span>
                      </>
                    ) : (
                      <span className="text-slate-400 text-xs print:hidden">
                        ✍️ ဒီနေရာတွင် လက်မှတ်ထိုးရန် နှိပ်ပါ
                      </span>
                    )}
                  </div>
                  {formData.signatureImage && (
                    <button
                      type="button"
                      onClick={handleClearSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center">
              <span className="w-24 text-right text-sm whitespace-nowrap mr-2">အမည်</span>
              {isPreview || !isEditable ? (
                <span className="flex-1 text-center font-medium border-0 border-b border-dashed border-slate-400 min-h-[24px]">
                  {formData.signName || formData.signerName || '-'}
                </span>
              ) : (
                <input
                  type="text"
                  name="signName"
                  value={formData.signName || formData.signerName || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({ ...prev, signName: val, signerName: val }));
                  }}
                  className="flex-1 bg-transparent border-0 border-b border-dashed border-slate-400 focus:outline-none focus:ring-0 text-center p-0 pb-0.5 text-sm font-medium text-slate-900"
                />
              )}
            </div>

            <p className="font-semibold text-center text-slate-900 pt-2">တွေ့ဆုံမေးမြန်းသူ</p>

            <div className="flex items-center">
              <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
              <div className="flex-grow">{renderDatePicker('submitDate')}</div>
            </div>
          </div>
        </div>

        {/* Form Action Buttons Component Integration */}
        <FormActionButtons
          isPreview={isPreview}
          isEditable={isEditable}
          isSubmitting={isSubmitting}
          isFormSigned={isFormSigned}
          isApproved={isApproved}
          targetGeoLevels={formData.targetGeoLevels}
          currentGeoLevel={currentUser?.scopeLevel}
          selectedGeoLevels={selectedGeoLevels}
          onGeoLevelChange={setSelectedGeoLevels}
          onCancel={handleBackNavigation}
          onSaveDraft={handleSaveDraft}
          onApproveAndSave={handleApproveAndSave}
          onSubmitToSuperior={handleSubmitToSuperior}
        />

        {/* <AttachmentSection
          attachments={formData.attachments}
          isEditable={isEditable}
          isPreview={isPreview}
          onFileChange={handleFileChange}
          onRemoveFile={handleRemoveFile}
        /> */}
      </form>
      {/* Signature Canvas Dialog */}
      {isSigning && isEditable && (
        <SignatureModal
          isOpen={isSigning}
          sigCanvasRef={sigCanvasRef}
          onClose={() => setIsSigning(false)}
          onClear={() => sigCanvasRef.current?.clear()}
          onSave={handleSaveSignature}
        />
      )}
    </div>
        
  </PageLayoutWrapper>
);
}